// ---------------------------------------------------------------------------
// YouAreYou (uru) — NotFoundPage
//
// Branded 404 error page shown when a user navigates to an invalid URL.
// Replaces the generic catch-all redirect to /dashboard with an informative
// page that helps users find their way back.
//
// Route: * (catch-all)
//
// Design notes:
//   No dedicated mockup exists. Follows the established design system:
//   centered card layout, primary blue accents, Public Sans font,
//   Material Symbols icons.
//
//   Provides:
//   - Clear "404" indicator with a friendly message
//   - Link back to Dashboard (authenticated users)
//   - Link to Login (unauthenticated users)
//   - Link to Support
//   - The current invalid path is shown so users can spot typos
// ---------------------------------------------------------------------------

import { Link, useLocation } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";

export default function NotFoundPage() {
  const location = useLocation();
  const { status } = useAuth();

  const isAuthenticated = status === "authenticated";

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-background-light px-4">
      <div className="w-full max-w-md text-center">
        {/* Icon */}
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-primary-50">
          <Icon
            name="explore_off"
            className="text-5xl text-primary-500"
          />
        </div>

        {/* 404 heading */}
        <h1 className="text-6xl font-extrabold tracking-tight text-primary-500">
          404
        </h1>
        <h2 className="mt-2 text-xl font-bold text-text-primary">
          Page not found
        </h2>
        <p className="mx-auto mt-2 max-w-sm text-sm text-text-secondary leading-relaxed">
          The page you&apos;re looking for doesn&apos;t exist or has been moved.
          Check the URL for typos, or use the links below to get back on track.
        </p>

        {/* Current path (so user can spot typos) */}
        <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5">
          <Icon name="link_off" className="text-sm text-text-muted" />
          <code className="text-xs font-mono text-text-muted">
            {location.pathname}
          </code>
        </div>

        {/* Action buttons */}
        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
            >
              <Icon name="home" className="text-lg" />
              Go to Dashboard
            </Link>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
            >
              <Icon name="login" className="text-lg" />
              Sign In
            </Link>
          )}

          <Link
            to={isAuthenticated ? "/connections" : "/register"}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-text-primary shadow-sm transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
          >
            <Icon
              name={isAuthenticated ? "people" : "how_to_reg"}
              className="text-lg text-primary-500"
            />
            {isAuthenticated ? "My Connections" : "Get Verified"}
          </Link>
        </div>

        {/* Help link */}
        <div className="mt-8 flex flex-col items-center gap-4">
          <p className="text-xs text-text-muted">
            Think this is a mistake?
          </p>
          <a
            href="/support"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors"
          >
            <Icon name="support_agent" className="text-lg" />
            Contact Support
          </a>
        </div>

        {/* Footer */}
        <div className="mt-12 flex items-center justify-center gap-4 text-xs text-text-muted">
          <a
            href="/privacy"
            className="hover:text-text-secondary transition-colors"
          >
            Privacy Policy
          </a>
          <span>·</span>
          <a
            href="/terms"
            className="hover:text-text-secondary transition-colors"
          >
            Terms of Service
          </a>
          <span>·</span>
          <a
            href="/support"
            className="hover:text-text-secondary transition-colors"
          >
            Support
          </a>
        </div>
      </div>
    </div>
  );
}
